import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    Box,
    Text,
    VStack,
    IconButton,
    Flex,
    HStack,
    Button,
    useBoolean,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import axios, { AxiosError } from 'axios';

import { ToastBoxContext } from '../../../contexts/ToastBoxContext';
import PageTitle from '../../../components/PageTitle';
import SelectField from '../../../components/SelectField';
import QuestionForm from './QuestionForm';
import { DimensionObj } from '../ManageDimensions';
import { TopicObject } from '../ManageTopics';
import apis from '../../../utils/apis';
import Loading from '../../../components/Loading';
import Placeholder from '../../../components/Placeholder';
import ToastBox from '../../../components/ToastBox';

export interface Question {
    id: string;
    description: string;
}

function ManageQuestions() {
    const {
        toast,
        showToast,
        onCloseToast,
    } = useContext(ToastBoxContext);
    // close toast message if open
    useEffect(
        () => {
            if (toast) {
                onCloseToast();
            }
        },
        [onCloseToast, toast],
    );

    const [loadingDimensions, setLoadingDimensions] = useBoolean();
    const [dimensionList, setDimensionList] = useState<DimensionObj[]>();
    const [selectedDimensionId, setSelectedDimensionId] = useState<string>('');

    const [loadingTopics, setLoadingTopics] = useBoolean();
    const [topicList, setTopicList] = useState<TopicObject[]>();
    const [selectedTopicId, setSelectedTopicId] = useState<string>('');

    const getSavedDimensions = useCallback(
        async () => {
            try {
                setLoadingDimensions.on();
                const apiUrl = '/susaf/dimension/all';
                const response = await apis.get(apiUrl);
                const dataResponse: DimensionObj[] = await response.data;
                if (dataResponse.length <= 0) {
                    setLoadingDimensions.off();
                    return;
                }
                setDimensionList(dataResponse);
                setLoadingDimensions.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR

                console.warn({ error });
            }
        },
        [setLoadingDimensions],
    );

    const dimensionOptions = useMemo(() => {
        if (!dimensionList) {
            return [];
        }
        const mappedDimensionList = dimensionList.map((dimension) => ({
            label: dimension.name,
            value: dimension.id ?? '',
        }));

        const defaultOption = {
            label: 'Select a dimension',
            value: '',
        };

        mappedDimensionList.splice(0, 0, defaultOption);

        return mappedDimensionList;
    }, [dimensionList]);

    const onSelectDimension = useCallback(
        (value: string) => {
            if (selectedTopicId) {
                setSelectedTopicId('');
            }
            setSelectedDimensionId(value);
        },
        [selectedTopicId],
    );

    useEffect(() => {
        getSavedDimensions();
    }, [getSavedDimensions]);

    const getSavedTopics = useCallback(
        async () => {
            try {
                if (!selectedDimensionId) {
                    return;
                }
                setLoadingTopics.on();
                const apiUrl = `/susaf/topic/all?dimensionId=${selectedDimensionId}`;
                const response = await apis.get(apiUrl);
                const dataResponse: TopicObject[] = await response.data;

                if (!dataResponse || dataResponse.length <= 0) {
                    setTopicList(undefined);
                    setLoadingTopics.off();
                    return;
                }
                setTopicList(dataResponse);
                setLoadingTopics.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR
                console.warn({ error });
            }
        },
        [selectedDimensionId, setLoadingTopics],
    );

    useEffect(() => {
        getSavedTopics();
    }, [getSavedTopics]);

    const topicOptions = useMemo(() => {
        if (!topicList) {
            return [];
        }
        const mappedTopicList = topicList.map((topic) => ({
            label: topic.name,
            value: topic.id ?? '',
        }));

        const defaultOption = {
            label: 'Select a topic',
            value: '',
        };

        mappedTopicList.splice(0, 0, defaultOption);

        return mappedTopicList;
    }, [topicList]);

    const onSelectTopic = useCallback(
        (value: string) => setSelectedTopicId(value as TopicObject['name']),
        [],
    );

    const [loadingQuestions, setLoadingQuestions] = useBoolean();
    const [questionList, setQuestionList] = useState<Question[]>();

    const getSavedQuestions = useCallback(
        async () => {
            try {
                if (!selectedDimensionId || !selectedTopicId) {
                    setQuestionList(undefined);
                    return;
                }
                setLoadingQuestions.on();
                const apiUrl = `/susaf/question/all?dimensionId=${selectedDimensionId}&&topicId=${selectedTopicId}`;
                const response = await apis.get(apiUrl);
                const dataResponse: Question[] = await response.data;

                if (!dataResponse || dataResponse.length <= 0) {
                    setQuestionList(undefined);
                    setLoadingQuestions.off();
                    return;
                }
                setQuestionList(dataResponse);
                setLoadingQuestions.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR
                console.warn({ error });
            }
        },
        [selectedDimensionId, selectedTopicId, setLoadingQuestions],
    );

    useEffect(() => {
        getSavedQuestions();
    }, [getSavedQuestions]);

    const title = 'Questions';
    const [editableQuestion, setEditableQuestion] = useState<Question>();

    const [formShown, setFormShown] = useBoolean();

    const onCancelAction = useCallback(
        () => {
            setFormShown.off();
            if (editableQuestion) {
                setEditableQuestion(undefined);
            }
        },
        [setFormShown, editableQuestion],
    );

    const onSaveQuestion = useCallback(
        async (formData: Question) => {
            try {
                if (!selectedDimensionId || !selectedTopicId) {
                    return;
                }
                const requestBody = formData;
                const apiUrl = `susaf/question/add?dimensionId=${selectedDimensionId}&&topicId=${selectedTopicId}`;
                const response = await apis.post(apiUrl, requestBody);
                if (response.status === 200) {
                    const responseData: Question = response.data;
                    setQuestionList((prevQuestion) => {
                        if (!prevQuestion || prevQuestion.length <= 0) {
                            return [responseData];
                        }
                        return [...prevQuestion, responseData];
                    });

                    const successToastComponent = toast && toast({
                        status: 'success',
                        isClosable: true,
                        variant: 'subtle',
                        id: undefined,
                        duration: null,
                        position: 'top',
                        render: () => (
                            <ToastBox
                                onCloseToast={onCloseToast}
                                title="Question added"
                                description="New question has been added successfully"
                                status="success"
                            />
                        ),
                    });
                    showToast(successToastComponent);
                    setFormShown.off();
                }
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    return;
                }
                const axiosError = error as AxiosError;
                const dataError = axiosError?.response?.data;
                console.warn({ dataError });
                const failureToastComponent = toast && toast({
                    status: 'error',
                    isClosable: true,
                    variant: 'subtle',
                    id: undefined,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Error adding"
                            description="Could not add. Try again"
                            status="error"
                        />
                    ),
                });

                showToast(failureToastComponent);
            }
        },
        [onCloseToast, selectedDimensionId, selectedTopicId, setFormShown, showToast, toast],
    );

    const onUpdateQuestion = useCallback(
        async (formData: Question) => {
            try {
                if (!editableQuestion || !selectedDimensionId || !selectedTopicId) {
                    return;
                }
                const requestBody = formData;
                const apiUrl = `susaf/question/edit?questionId=${editableQuestion.id}&dimensionId=${selectedDimensionId}&&topicId=${selectedTopicId}`;
                const response = await apis.put(apiUrl, requestBody);
                if (response.status === 200) {
                    const updatedQuestionData: Question = response.data;
                    setQuestionList((prevQuestion) => {
                        if (!prevQuestion || prevQuestion.length <= 0) {
                            return [updatedQuestionData];
                        }
                        const tmpQuestionList = [...prevQuestion];
                        const updatedQuestionList = tmpQuestionList.map(
                            (item) => (item.id === updatedQuestionData.id
                                ? updatedQuestionData : item),
                        );
                        return updatedQuestionList;
                    });

                    const successToastComponent = toast && toast({
                        status: 'success',
                        isClosable: true,
                        variant: 'subtle',
                        id: undefined,
                        duration: null,
                        position: 'top',
                        render: () => (
                            <ToastBox
                                onCloseToast={onCloseToast}
                                title="Question updated"
                                description="Question has been updated successfully"
                                status="success"
                            />
                        ),
                    });
                    showToast(successToastComponent);
                    setFormShown.off();
                    setEditableQuestion(undefined);
                }
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    return;
                }
                const axiosError = error as AxiosError;
                const dataError = axiosError?.response?.data;
                console.warn({ dataError });
                const failureToastComponent = toast && toast({
                    status: 'error',
                    isClosable: true,
                    variant: 'subtle',
                    id: undefined,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Error adding"
                            description="Could not add. Try again"
                            status="error"
                        />
                    ),
                });

                showToast(failureToastComponent);
            }
        },
        [
            editableQuestion,
            onCloseToast,
            selectedDimensionId,
            selectedTopicId,
            setFormShown,
            showToast,
            toast,
        ],
    );

    return (
        <VStack>
            <PageTitle
                title={title}
            />
            {loadingDimensions && (
                <Box width="100%">
                    <Loading message="Loading data" />
                </Box>
            )}
            {!loadingDimensions && dimensionOptions.length > 0 && (
                <Flex
                    width="100%"
                    alignItems="flex-end"
                    justifyContent="space-between"
                >
                    <Box width="80%">
                        <VStack>
                            <SelectField
                                options={dimensionOptions}
                                label="Select Dimension"
                                onSelectOption={onSelectDimension}
                                value={selectedDimensionId}
                            />
                            {selectedDimensionId && topicOptions.length > 0 && (
                                <SelectField
                                    options={topicOptions}
                                    label="Select Topic"
                                    onSelectOption={onSelectTopic}
                                    value={selectedTopicId}
                                />
                            )}
                        </VStack>
                    </Box>
                    {selectedDimensionId && selectedTopicId && (
                        <Button
                            type="reset"
                            colorScheme="brand"
                            variant="outline"
                            letterSpacing={1}
                            onClick={setFormShown.toggle}
                            width="15%"
                            height={12}
                        >
                            {formShown ? 'Close Form' : 'Add Question'}
                        </Button>
                    )}
                </Flex>
            )}
            {dimensionList && dimensionList.length > 0 && !selectedDimensionId && (
                <Box width="100%">
                    <Placeholder text="Select dimension and topic to be able to add question" />
                </Box>
            )}
            {selectedDimensionId && !selectedTopicId && topicList && topicList.length > 0 && (
                <Box width="100%">
                    <Placeholder text="Select topic to be able to add question" />
                </Box>
            )}
            {selectedDimensionId && !loadingDimensions && loadingTopics && (
                <Box width="100%">
                    <Loading message="Loading Topic list" />
                </Box>
            )}

            {!loadingTopics && selectedDimensionId && topicOptions.length === 0 && (
                <Placeholder text="Please make sure you have topic for this dimension." />
            )}
            {formShown && (
                <Box
                    width="100%"
                    alignItems="flex-start"
                >
                    <QuestionForm
                        onSaveAction={onSaveQuestion}
                        onCancelAction={onCancelAction}
                        editableQuestion={undefined}
                    />
                </Box>
            )}
            {loadingQuestions && (
                <Placeholder text="Loading question List" />
            )}
            {selectedTopicId && !loadingQuestions && (!questionList || questionList.length <= 0)
                && (
                    <Placeholder text="No questions available for the selected dimension and topic." />
                )}
            {!loadingQuestions && questionList && questionList.length > 0 && questionList.map(
                (question) => (
                    <Box
                        key={question.id}
                        borderWidth={1}
                        width="100%"
                        padding={4}
                    >
                        <HStack>
                            <Text width="100%">
                                {question.description}
                            </Text>
                            <HStack
                                justifyContent="space-between"
                                marginX={10}
                            >
                                <IconButton
                                    aria-label={`edit-question-${question.id}`}
                                    icon={<EditIcon />}
                                    height={6}
                                    width={6}
                                    fontSize={12}
                                    onClick={() => setEditableQuestion(question)}
                                />
                                <IconButton
                                    aria-label={`delete-question-${question.id}`}
                                    icon={<DeleteIcon />}
                                    height={6}
                                    width={6}
                                    background="red.600"
                                    color="white"
                                    fontSize={12}
                                />
                            </HStack>
                        </HStack>
                        {editableQuestion?.id === question.id && (
                            <Box mt={8}>
                                <QuestionForm
                                    onSaveAction={onUpdateQuestion}
                                    editableQuestion={editableQuestion}
                                    onCancelAction={onCancelAction}
                                />
                            </Box>
                        )}
                    </Box>
                ),
            )}
        </VStack>
    );
}

export default ManageQuestions;
